import {Api, StringUtil} from "@mcleod/core";
import {Snackbar} from "@mcleod/components";
import {ImageName} from "@mcleod/images";

/**
 * Tests a SQL query against the API and updates the UI accordingly
 * @param component - The component with a labelQueryStatus to update
 * @param id - The row id
 * @param sqlQuery - The SQL query string to test (optional, used for local validation)
 * @returns Promise that resolves when the test is complete
 */
export async function testSqlQuery(component: any, id: string, sqlQuery?: string): Promise<void> {
    if (!component.labelQueryStatus) {
        return;
    }

    // Helper function to set status properties and avoid repetition
    const setQueryStatus = (isError: boolean, message: string, showSnackbar: boolean = false) => {
        component.labelQueryStatus.imageColor = isError ? "error" : "success";
        component.labelQueryStatus.imageName = isError ? ImageName.circleErrorSolid : ImageName.circleCheck;
        component.labelQueryStatus.visible = true;
        component.labelQueryStatus.tooltip = message;

        if (showSnackbar) {
            Snackbar.showWarningSnackbar(message);
        }
    };

    // --- Frontend validation before API call ---
    const validationError = validateSqlQuery(sqlQuery || "");
    if (validationError) {
        setQueryStatus(true, validationError, true);
        return;
    }

    try {
        const response = await Api.post("lme/powerbroker/tia-validations", {is_query: true, id: id, query: sqlQuery});
        if (response && response.data && response.data.length > 0) {
            const responseData = response.data[0];

            if (responseData.error) {
                // Error from API
                setQueryStatus(true, `Error: ${responseData.error}`, true);
            } else if (responseData.response) {
                // Valid response from API - check if it's a valid query result
                const queryResult = responseData.response;
                setQueryStatus(false, `Row count: ${queryResult}`);
            } else {
                // Unexpected response format
                setQueryStatus(true, "Invalid response from server");
            }
        } else {
            // Empty or missing response
            setQueryStatus(true, "No response from server");
        }
    } catch (error) {
        // Exception during API call
        const errorMessage = `SQL Query Error: ${error?.message || "Unknown error"}`;
        setQueryStatus(true, errorMessage, true);
    }
}

/**
 * Validates a SQL query string for forbidden statements, keywords, and symbols.
 * Returns an error message if invalid, or null if valid.
 */
export function validateSqlQuery(query: string): string | null {
    if (!query || typeof query !== "string") return "Query must be provided for SQL execution.";
    const forbiddenKeywords = [
        "DELETE", "ALTER", "USE", "ADD", "UPDATE", "DROP", "MERGE", "CREATE", "TRUNCATE", "GRANT", "REVOKE", "COMMIT", "ROLLBACK", "RENAME", "MODIFY", "COLLATE",
        "REPLACE", "SCHEMA", "TABLE_SCHEMA", "TABLE_NAME", "COLUMN_NAME", "DATA_TYPE", "INFORMATION_SCHEMA", "EXEC", "EXECUTE", "CAST", "CONVERT",
        "UNION", "GO", "XP_", "DECLARE", "ENCRYPT", "DECRYPT", "SYSOBJECTS", "SYS"
    ];
    // Remove quoted substrings (single and double quotes)
    const removeQuoted = (sql: string) => sql
        .replace(/'([^'\\]*(\\.[^'\\]*)*)'/g, "")
        .replace(/"([^"\\]*(\\.[^"\\]*)*)"/g, "");
    const unquoted = removeQuoted(query);
    // 1. Block forbidden symbols (double dash, block comments) outside of quotes
    if (unquoted.includes("--") || unquoted.includes("/*")) {
        return "Invalid SQL Query: forbidden symbols (double dash or block comments) detected.";
    }
    // 2. Block forbidden keywords (case-insensitive, word boundary, not inside quotes)
    for (const keyword of forbiddenKeywords) {
        const pattern = new RegExp(`\\b${keyword.replace(/\./g, "\\.")}\\b`, "i");
        if (pattern.test(unquoted)) {
            return `Invalid SQL Query: ${keyword} statements are not allowed.`;
        }
    }
    // 3. Only allow SELECT or INSERT as the first statement (ignore whitespace/comments)
    let cleaned = query.trim();
    cleaned = cleaned.replace(/^\/\*[\s\S]*?\*\//, "").trim(); // Remove leading block comments
    cleaned = cleaned.replace(/^(--.*?$\s*)+/m, "").trim(); // Remove leading line comments
    const firstWordMatch = cleaned.match(/^(\w+)/);
    if (!firstWordMatch || !["SELECT", "INSERT"].includes(firstWordMatch[1].toUpperCase())) {
        return "Only SELECT and INSERT statements are allowed.";
    }
    return null;
}

export function formatSqlQuery(query: string): string {
    if (!query) return "";

    if (StringUtil.equalsIgnoreCase(query, "x")) {
        return "--";
    }

    // Basic SQL formatter: adds newlines before major SQL clauses and uppercases keywords
    const keywords = [
        "SELECT", "INSERT", "INTO", "UPDATE", "DELETE", "FROM", "WHERE", "GROUP BY", "ORDER BY",
        "INNER JOIN", "LEFT JOIN", "RIGHT JOIN", "FULL JOIN", "CROSS JOIN", "JOIN",
        "AND", "OR", "ON", "AS", "VALUES", "SET", "HAVING", "LIMIT", "OFFSET", "DISTINCT", "COUNT",
        "SUM", "AVG", "MIN", "MAX"
    ];
    let formatted = query.trim();

    // Add newlines before keywords and uppercase them
    keywords.forEach(keyword => {
        const regex = new RegExp(`\\b${keyword.replace(" ", "\\s+")}\\b`, "gi");
        formatted = formatted.replace(regex, match => `\n${match.toUpperCase()}`);
    });

    // Clean up multiple newlines and trim again
    formatted = formatted.replace(/\n+/g, "\n").trim();

    return formatted;
}

